import React from "react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f5f7fa]" data-testid="terms-of-service-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-10">
          <button onClick={() => navigate(-1)} className="text-sm text-[#4a9eff] hover:underline mb-4 inline-block" data-testid="terms-back-btn">
            &larr; Back
          </button>
          <h1 className="text-3xl font-bold text-[#1e3a5f] tracking-tight">Terms of Service</h1>
          <p className="text-sm text-[#5a6577] mt-2">Last updated: March 29, 2026</p>
        </div>

        <div className="bg-white rounded-xl border border-[#e4e8ee] p-8 space-y-8 text-[#3d4f63] text-sm leading-relaxed">
          <p>
            Welcome to Opturna ("Company," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the Opturna platform, website, and related services (collectively, the "Service"). By creating an account or using the Service, you agree to be bound by these Terms.
          </p>
          <p className="font-medium text-[#1e3a5f]">If you do not agree to these Terms, do not use the Service.</p>

          {/* Section 1 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">1. Eligibility</h2>
            <p className="mb-2">You must be at least 18 years old and have the legal capacity to enter into a binding agreement to use Opturna. By using the Service, you represent and warrant that you meet these requirements.</p>
            <p>If you are using the Service on behalf of a business entity, you represent that you have the authority to bind that entity to these Terms.</p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">2. Account Registration</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>You must provide accurate, complete, and current information when creating an account.</li>
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
            </ul>
            <p className="mt-3">We reserve the right to suspend or terminate accounts that violate these Terms or contain false information.</p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">3. Service Description</h2>
            <p className="mb-2">Opturna is a business financial management platform that provides tools for:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Automated bookkeeping and transaction categorization</li>
              <li>Tax planning and estimated tax calculations</li>
              <li>Profit-first allocation management</li>
              <li>Deduction tracking and identification</li>
              <li>Cash flow projections and financial reporting</li>
              <li>AI-powered financial insights and recommendations</li>
              <li>Receipt scanning and document management</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">4. Educational Use Disclaimer</h2>
            <div className="p-4 bg-[#fff8f0] rounded-lg border border-[#f0d8b5]">
              <p className="font-semibold text-[#1e3a5f] mb-2">Important Notice:</p>
              <p className="mb-2">All financial insights, tax calculations, deduction suggestions, and AI-generated recommendations provided by Opturna are for <strong>educational and informational purposes only</strong>.</p>
              <p className="mb-1 font-medium text-[#1e3a5f]">Opturna does NOT:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Provide legal, tax, accounting, or investment advice</li>
                <li>Guarantee tax savings, deductions, or financial outcomes</li>
                <li>Act as a licensed CPA, Enrolled Agent, tax attorney, or financial advisor</li>
                <li>Replace professional financial consultation</li>
              </ul>
            </div>
            <p className="mt-3">You are solely responsible for consulting with qualified professionals before making financial, tax, or business decisions based on information provided by the Service.</p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">5. Subscriptions & Billing</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Free Trial:</strong> New users may be eligible for a 7-day free trial. You will not be charged during the trial period.</li>
              <li><strong>Billing Cycle:</strong> Subscriptions are billed on a recurring basis (monthly or annually) depending on the plan you select.</li>
              <li><strong>Automatic Renewal:</strong> Your subscription will automatically renew at the end of each billing period unless you cancel before the renewal date.</li>
              <li><strong>Cancellation:</strong> You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. No refunds are issued for partial billing periods.</li>
              <li><strong>Price Changes:</strong> We reserve the right to adjust pricing with 30 days' prior notice. Continued use after a price change constitutes acceptance of the new pricing.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">6. Acceptable Use</h2>
            <p className="mb-2">You agree not to use the Service to:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Violate any applicable law, regulation, or third-party rights</li>
              <li>Upload malicious content, viruses, or harmful code</li>
              <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
              <li>Reverse-engineer, decompile, or disassemble any part of the Service</li>
              <li>Use the Service for any fraudulent or deceptive purpose</li>
              <li>Resell, sublicense, or redistribute access to the Service without authorization</li>
            </ul>
            <p className="mt-2">Violation of these terms may result in immediate account suspension or termination.</p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">7. User Content & Data</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Ownership:</strong> You retain ownership of all data, documents, and content you upload to the Service ("User Content").</li>
              <li><strong>License:</strong> By uploading User Content, you grant Opturna a limited, non-exclusive license to process and store your content solely for the purpose of providing the Service.</li>
              <li><strong>Accuracy:</strong> You are responsible for the accuracy and completeness of the data you provide. Opturna is not liable for errors resulting from inaccurate user inputs.</li>
              <li><strong>Data Export:</strong> You may request export of your data at any time by contacting our support team.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">8. Intellectual Property</h2>
            <p className="mb-2">The Service, including all software, design, content, trademarks, and intellectual property, is owned by Opturna Tech Inc. and protected under applicable intellectual property laws.</p>
            <p>You may not copy, modify, distribute, or create derivative works from any part of the Service without prior written consent.</p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">9. Third-Party Services</h2>
            <p className="mb-2">The Service may integrate with or link to third-party services, including:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Payment processors (e.g., Stripe)</li>
              <li>Banking data providers (e.g., Plaid)</li>
              <li>AI and analytics tools</li>
              <li>Cloud infrastructure providers</li>
            </ul>
            <p className="mt-2">Your use of third-party services is subject to their respective terms and privacy policies. Opturna is not responsible for the practices or content of third-party services.</p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">10. Limitation of Liability</h2>
            <div className="p-4 bg-[#f5f7fa] rounded-lg border border-[#e4e8ee]">
              <p className="mb-2">To the maximum extent permitted by law, Opturna and its officers, directors, employees, and agents shall not be liable for:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Errors in tax calculations, deduction estimates, or financial projections</li>
                <li>Decisions made based on information provided by the Service</li>
                <li>Service interruptions, downtime, or data loss</li>
              </ul>
              <p className="mt-3">Our total liability for any claim arising from or related to the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">11. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless Opturna and its affiliates from any claims, damages, losses, or expenses (including reasonable attorney's fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.</p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">12. Termination</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>You may close your account at any time by contacting us.</li>
              <li>We may suspend or terminate your access for violation of these Terms, non-payment, or at our discretion with reasonable notice.</li>
              <li>Upon termination, your right to use the Service ceases immediately. We may retain certain data as required by law.</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">13. Dispute Resolution</h2>
            <p className="mb-2">Any disputes arising from these Terms or your use of the Service shall be resolved through:</p>
            <ol className="list-decimal ml-6 space-y-1">
              <li><strong>Informal Resolution:</strong> We encourage you to contact us first to resolve any dispute informally.</li>
              <li><strong>Binding Arbitration:</strong> If informal resolution fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association (AAA), conducted in Dover, Delaware.</li>
            </ol>
            <p className="mt-2">You agree to waive your right to participate in class action lawsuits or class-wide arbitration.</p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">14. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions.</p>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">15. Changes to These Terms</h2>
            <p>We reserve the right to update these Terms at any time. Material changes will be communicated via email or an in-app notification at least 30 days in advance. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="text-lg font-semibold text-[#1e3a5f] mb-3">16. Contact Us</h2>
            <p className="mb-2">If you have questions about these Terms of Service, contact:</p>
            <div className="p-4 bg-[#f5f7fa] rounded-lg border border-[#e4e8ee]">
              <p className="font-semibold text-[#1e3a5f]">Opturna Tech Inc.</p>
              <p className="text-[#5a6577]">8 The Green Suite 8882, Dover, DE 19901</p>
              <p><a href="mailto:Admin@opturnatech.com" className="text-[#4a9eff] hover:underline">Admin@opturnatech.com</a></p>
            </div>
          </section>
        </div>

        <p className="text-center text-xs text-[#8b95a5] mt-8">Opturna Tech Inc. All rights reserved.</p>
      </div>
    </div>
  );
};

export default TermsOfService;
